import {createContext, useCallback, useContext, useRef, useState} from 'react';

const ToastContext = createContext(null);

let nextId = 0;
const MAX_TOASTS = 3;
const DISMISS_MS = 5000;

export function useToast() {
    const showToast = useContext(ToastContext);
    if (!showToast) throw new Error('useToast must be used within ToastProvider');
    return showToast;
}

export function ToastProvider({children}) {
    const [toasts, setToasts] = useState([]);
    const timersRef = useRef({});

    const dismissToast = useCallback(id => {
        clearTimeout(timersRef.current[id]);
        delete timersRef.current[id];
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const showToast = useCallback(
        (message, type = 'error') => {
            setToasts(prev => {
                if (prev.some(t => t.message === message)) return prev;
                const id = ++nextId;
                timersRef.current[id] = setTimeout(() => dismissToast(id), DISMISS_MS);
                const next = [...prev, {id, message, type}];
                return next.length > MAX_TOASTS ? next.slice(-MAX_TOASTS) : next;
            });
        },
        [dismissToast],
    );

    return (
        <ToastContext.Provider value={showToast}>
            {children}
            {toasts.length > 0 && (
                <div className="toast-container" role="status" aria-live="polite">
                    {toasts.map(t => (
                        <div key={t.id} className={`toast toast--${t.type}`}>
                            <span className="toast-message">{t.message}</span>
                            <button className="toast-dismiss" onClick={() => dismissToast(t.id)} aria-label="Dismiss">
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </ToastContext.Provider>
    );
}
